import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import receivablesService from '../services/receivablesService';
import projectsService from '../services/projectsService';
import inventoryService from '../services/inventoryService';
import {
  Search, Filter, DollarSign, Clock, AlertTriangle, CheckCircle, XCircle,
  Calendar, ChevronDown, ChevronUp, Plus, Coins, RefreshCcw, X, Loader,
  TrendingUp, TrendingDown, CreditCard, Building, FileText, ShieldCheck
} from 'lucide-react';
import toast from 'react-hot-toast';

const RecebimentosDashboard = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('pending');
  const [filter, setFilter] = useState({ search: '', dateFrom: '', dateTo: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showOnlyInitialBalances, setShowOnlyInitialBalances] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [receiveForm, setReceiveForm] = useState({ actual_amount: '', received_date: '', payment_method: 'transfer', notes: '', supplier_id: '' });

  // Buscar fornecedores
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers-for-receivables'],
    queryFn: () => inventoryService.getSuppliers(),
    enabled: showReceiveModal
  });
  const suppliers = suppliersData?.data || [];

  // Resetar para a primeira página ao alterar aba, busca ou filtro de saldos iniciais
  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedSearch, showOnlyInitialBalances]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filter.search);
    }, 300);
    return () => clearTimeout(timer);
  }, [filter.search]);

  // Buscar dados com paginação e filtro de saldos iniciais no servidor
  const { data: receivablesData, isLoading } = useQuery({
    queryKey: ['receivables', activeTab, debouncedSearch, showOnlyInitialBalances, page, pageSize],
    queryFn: () => receivablesService.getAll({
      status: activeTab === 'pending' ? 'PENDING' : activeTab === 'overdue' ? 'OVERDUE' : activeTab === 'received' ? 'RECEIVED' : undefined,
      search: debouncedSearch,
      only_initial_balances: showOnlyInitialBalances ? 'true' : undefined,
      page,
      limit: pageSize
    })
  });

  const { data: statsData } = useQuery({
    queryKey: ['receivables-stats'],
    queryFn: () => receivablesService.getStats()
  });

  const receivables = receivablesData?.data?.data?.receivables || receivablesData?.data?.receivables || receivablesData?.receivables || [];
  const summary = receivablesData?.data?.data?.summary || receivablesData?.data?.summary || receivablesData?.summary || {};
  const pagination = receivablesData?.data?.pagination || receivablesData?.data?.data?.pagination || receivablesData?.pagination || {};
  const totalRecords = pagination.total ?? (receivablesData?.data?.total || receivablesData?.total || receivables.length);
  const totalPages = pagination.totalPages || Math.max(1, Math.ceil(totalRecords / pageSize));

  // Mutation para receber parcela
  const receiveMutation = useMutation({
    mutationFn: ({ id, data }) => receivablesService.receive(id, data),
    onSuccess: () => {
      toast.success('Recebimento registrado!');
      queryClient.invalidateQueries(['receivables']);
      queryClient.invalidateQueries(['receivables-stats']);
      setShowReceiveModal(false);
      setSelectedParcel(null);
      setReceiveForm({ actual_amount: '', received_date: '', payment_method: 'transfer', notes: '' });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao registrar');
    }
  });

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const getStatusBadge = (status) => {
    const config = {
      PENDING: { label: 'Pendente', bg: 'bg-yellow-100', text: 'text-yellow-800' },
      OVERDUE: { label: 'Atrasado', bg: 'bg-red-100', text: 'text-red-800' },
      RECEIVED: { label: 'Recebido', bg: 'bg-green-100', text: 'text-green-800' },
      COMPLETED: { label: 'Recebido', bg: 'bg-green-100', text: 'text-green-800' },
      CANCELLED: { label: 'Cancelado', bg: 'bg-gray-100', text: 'text-gray-800' }
    };
    const c = config[status] || config.PENDING;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>{c.label}</span>;
  };

  const handleOpenReceive = (parcel) => {
    setSelectedParcel(parcel);
    setReceiveForm({
      actual_amount: parcel.expected_amount,
      received_date: new Date().toISOString().split('T')[0],
      payment_method: 'transfer',
      notes: '',
      supplier_id: ''
    });
    setShowReceiveModal(true);
  };

  const handleReceive = () => {
    receiveMutation.mutate({ id: selectedParcel.id, data: receiveForm });
  };

  // Calcular estatísticas
  const stats = statsData?.data?.data || statsData?.data || {};
  const byStatus = stats.by_status || [];
  const totalPending = byStatus.find(s => s.status === 'PENDING')?.total_expected || 0;
  const totalOverdue = byStatus.find(s => s.status === 'OVERDUE')?.total_expected || 0;
  const totalReceived = byStatus.filter(s => s.status === 'RECEIVED' || s.status === 'COMPLETED').reduce((acc, s) => acc + parseFloat(s.total_received || 0), 0);

  const pendingCount = byStatus.find(s => s.status === 'PENDING')?.count || 0;
  const overdueCount = byStatus.find(s => s.status === 'OVERDUE')?.count || 0;
  const receivedCount = byStatus.filter(s => s.status === 'RECEIVED' || s.status === 'COMPLETED').reduce((acc, s) => acc + parseInt(s.count || 0), 0);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Coins className="w-8 h-8 text-green-600" />
          Recebimentos
        </h1>
        <p className="text-gray-600">Gerencie as parcelas e recebimentos dos projetos</p>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4 border-l-4 border-yellow-500">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-sm text-gray-500">Pendentes</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totalPending)}</p>
              <p className="text-xs text-gray-500">{pendingCount} parcela(s)</p>
            </div>
          </div>
        </div>

        <div className="card p-4 border-l-4 border-red-500">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-sm text-gray-500">Atrasadas</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totalOverdue)}</p>
              <p className="text-xs text-gray-500">{overdueCount} parcela(s)</p>
            </div>
          </div>
        </div>

        <div className="card p-4 border-l-4 border-green-500">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-sm text-gray-500">Recebidas</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totalReceived)}</p>
              <p className="text-xs text-gray-500">{receivedCount} parcela(s)</p>
            </div>
          </div>
        </div>

        <div className="card p-4 border-l-4 border-blue-500">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-sm text-gray-500">Taxa Inadimpl.</p>
              <p className="text-xl font-bold text-gray-900">{stats.default_rate || 0}%</p>
              <p className="text-xs text-gray-500">dos pagamentos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Abas */}
      <div className="flex border-b mb-4">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 font-medium flex items-center gap-2 ${activeTab === 'pending' ? 'border-b-2 border-yellow-500 text-yellow-600' : 'text-gray-500'}`}
        >
          <Clock className="w-4 h-4" />
          Pendentes ({pendingCount})
        </button>
        <button
          onClick={() => setActiveTab('overdue')}
          className={`px-4 py-2 font-medium flex items-center gap-2 ${activeTab === 'overdue' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500'}`}
        >
          <AlertTriangle className="w-4 h-4" />
          Atrasadas ({overdueCount})
        </button>
        <button
          onClick={() => setActiveTab('received')}
          className={`px-4 py-2 font-medium flex items-center gap-2 ${activeTab === 'received' ? 'border-b-2 border-green-500 text-green-600' : 'text-gray-500'}`}
        >
          <CheckCircle className="w-4 h-4" />
          Recebidas ({receivedCount})
        </button>
      </div>

      {/* Filtros */}
      <div className="card p-4 mb-4">
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por projeto ou cliente..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showOnlyInitialBalances}
              onChange={(e) => setShowOnlyInitialBalances(e.target.checked)}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Saldos Iniciais
            </span>
          </label>
          <span className="text-xs text-gray-500">
            ({totalRecords} registros)
          </span>
        </div>
      </div>

      {/* Tabela de Parcelas */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : receivables.length === 0 ? (
          <div className="text-center p-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Nenhuma parcela encontrada</p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Projeto</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Cliente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Parcela</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Vencimento</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Valor</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {receivables
                  .filter(r => activeTab !== 'received' || r.payment_status === 'RECEIVED' || r.payment_status === 'COMPLETED')
                  .map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {r.initial_balance_id && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 mb-1">
                          Saldo Inicial
                        </span>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{r.project_code || `#${r.project_id}`}</p>
                        <p className="text-sm text-gray-500">{r.project_name}</p>
                        {(r.has_delivery_acceptance || r.current_stage === 'DELIVERED') && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 mt-1"
                            title="Obra entregue com Termo de Aceite assinado pelo cliente"
                          >
                            <ShieldCheck className="w-3 h-3 text-emerald-600" /> Obra Entregue
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.client_name || r.client_company || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.installment_number ? `Parcela ${r.installment_number}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDate(r.expected_date)}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {formatCurrency(r.expected_amount)}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(r.payment_status)}
                    </td>
                    <td className="px-4 py-3">
                      {r.payment_status === 'RECEIVED' || r.payment_status === 'COMPLETED' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Pago/Recebido
                        </span>
                      ) : r.payment_status !== 'CANCELLED' ? (
                        <button
                          onClick={() => handleOpenReceive(r)}
                          className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm"
                        >
                          <Coins className="w-4 h-4" />
                          Receber
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Barra de Paginação */}
            {totalRecords > 0 && (
              <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200 bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-700">Mostrar</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="border border-gray-300 rounded px-2 py-1 text-xs bg-white text-gray-800 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-xs text-gray-700">
                    Itens {Math.min(((page - 1) * pageSize) + 1, totalRecords)} a {Math.min(page * pageSize, totalRecords)} de {totalRecords}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-gray-700"
                  >
                    Anterior
                  </button>

                  <span className="text-xs text-gray-700 px-2 font-medium">
                    Página {page} de {totalPages}
                  </span>

                  <button
                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={page >= totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-gray-700"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Recebimento */}
      {showReceiveModal && selectedParcel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Registrar Recebimento</h3>

            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Projeto</p>
                <p className="font-medium">{selectedParcel.project_name}</p>
                <p className="text-sm text-gray-500">{selectedParcel.client_name}</p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Valor Esperado</p>
                <p className="font-medium text-lg">{formatCurrency(selectedParcel.expected_amount)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Recebido *
                </label>
                <input
                  type="number"
                  value={receiveForm.actual_amount}
                  onChange={(e) => setReceiveForm({ ...receiveForm, actual_amount: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data do Recebimento
                </label>
                <input
                  type="date"
                  value={receiveForm.received_date}
                  onChange={(e) => setReceiveForm({ ...receiveForm, received_date: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forma de Pagamento
                </label>
                <select
                  value={receiveForm.payment_method}
                  onChange={(e) => setReceiveForm({ ...receiveForm, payment_method: e.target.value, supplier_id: '' })}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="transfer">Transferência</option>
                  <option value="cash">Dinheiro</option>
                  <option value="pix">PIX</option>
                  <option value="pix_direct">PIX direto ao fornecedor</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="debit_card">Cartão de Débito</option>
                  <option value="check">Cheque</option>
                  <option value="check_direct">Cheque direto ao fornecedor</option>
                  <option value="boleto">Boleto</option>
                </select>
              </div>

              {(receiveForm.payment_method === 'pix_direct' || receiveForm.payment_method === 'check_direct') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fornecedor (pagamento direto) *
                  </label>
                  <select
                    value={receiveForm.supplier_id}
                    onChange={(e) => setReceiveForm({ ...receiveForm, supplier_id: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    required
                  >
                    <option value="">Selecione o fornecedor</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={receiveForm.notes}
                  onChange={(e) => setReceiveForm({ ...receiveForm, notes: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  rows={2}
                  placeholder="Observações opcionais..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowReceiveModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleReceive}
                disabled={receiveMutation.isPending || !receiveForm.actual_amount}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {receiveMutation.isPending ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Confirmar Recebimento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecebimentosDashboard;